import {
  Component,
  ViewEncapsulation,
  input,
  signal,
  computed,
  effect,
  CreateEffectOptions,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { LoaderModule } from '@progress/kendo-angular-indicators';
import { NgxEchartsModule } from 'ngx-echarts';
import { EChartsOption } from 'echarts';
import { DonutChartConfig, DonutChartData } from 'src/app/core/charts/configs/donut-chart.config';
import { CheckpointProtectionResponse } from 'src/app/core/models/analytics/es-models';
import { BaseComponent } from 'src/app/core/shared/common/base.component';
import { getCSSVariableColor } from 'src/app/core/charts/utils/chart-colors';

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-es-cp-protection',
  standalone: true,
  imports: [CommonModule, NgxEchartsModule, LoaderModule],
  templateUrl: './es-cp-protection.component.html',
  styleUrl: './es-cp-protection.component.scss'
})
export class EsCpProtectionComponent extends BaseComponent {
  public checkpointProtectionData = input<CheckpointProtectionResponse>();
  public chartData = signal<Array<{ name: string; value: number }>>([]);
  public isLoading = signal(true);

  constructor() {
    super();
    this.initEffect();
  }

  initEffect() {
    effect(
      () => {
        const data = this.checkpointProtectionData();
        if (data?.chartData) {
          this.chartData.set(data.chartData);
          this.isLoading.set(false);
        } else {
          this.chartData.set([]);
          this.isLoading.set(true);
        }
      },
      { allowSignalWrites: true } as CreateEffectOptions
    );
  }

  // ECharts configuration using centralized config
  chartOptions = computed((): EChartsOption => {
    const data = this.chartData();
    if (!data || data.length === 0) {
      return this.getEmptyChartOptions();
    }

    const chartData: DonutChartData = {
      data: data.map((item, index) => ({
        name: item.name,
        value: item.value,
        color: getCSSVariableColor(`--chart-color-${index + 1}`) || `var(--chart-color-${index + 1})`,
      })),
      innerRadius: '45%',
      outerRadius: '70%',
      showLabels: true,
      labelFormatter: 'nameAndValue'
    };

    // Thousands separator only on this card; DonutChartConfig is shared.
    const options = DonutChartConfig.create(chartData);
    const formatValue = (value: number) => value.toLocaleString('en-US');
    const series = (options.series as any[])[0];
    series.label = {
      ...series.label,
      formatter: (params: any) => `${params.name}\n${formatValue(params.value)}`,
    };
    options.tooltip = {
      ...(options.tooltip as any),
      formatter: (params: any) =>
        `${params.name}: ${formatValue(params.value)}<br/>(${params.percent}%)`,
    };
    return options;
  });


  private getEmptyChartOptions(): EChartsOption {
    const emptyData: DonutChartData = {
      data: [],
    };

    return DonutChartConfig.create(emptyData, {
      tooltip: {
        formatter: () => 'No data available',
      },
    });
  }

  get totalThreats(): number {
    return this.chartData().reduce((sum, item) => sum + item.value, 0);
  }

  get inboxThreats(): number {
    return this.chartData().find(item => item.name === 'Inbox')?.value || 0;
  }

  get junkThreats(): number {
    return this.chartData().find(item => item.name === 'Junk folder')?.value || 0;
  }
}
